import type { JSX } from 'react';
import { FONT_FAMILIES } from '@/catalogs/fonts';
import { MAX_RASTER_SIDE } from '@/core/export/export';
import { MIN_QUIET_ZONE } from '@/core/project/types';
import { MIN_MODULE_PX } from '@/core/layout/geometry';
import { APP_VERSION, BUILD_TIME } from './version';
import styles from './HowItWorks.module.css';

/**
 * Страница «Как это работает».
 *
 * Формулировки намеренно осторожные: мы описываем то, что действительно
 * делаем, и отдельно называем то, чего не обещаем.
 */
export function HowItWorks(): JSX.Element {
  return (
    <article className={styles.page}>
      <h1 className={styles.h1}>Как это работает</h1>

      <section className={styles.section}>
        <h2 className={styles.h2}>Что попадает в код</h2>
        <p>
          В QR записывается ровно та строка, которую вы подтвердили. Сервис не подменяет её
          собственной короткой ссылкой, не добавляет UTM-метки и не вставляет промежуточные
          переходы. Поэтому после скачивания файла наша доступность больше не нужна: код
          расшифровывается сам по себе.
        </p>
        <p className={styles.caution}>
          Чего мы не обещаем: что сайт по ссылке безопасен. Мы проверяем синтаксис адреса и не
          подменяем его — это не проверка репутации ресурса.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Где происходят вычисления</h2>
        <p>
          Кодирование, вёрстка, растеризация и проверка выполняются на вашем устройстве. Содержимое
          полей не уходит в аналитику, в отчёты об ошибках, в параметры адреса страницы и в сторонние
          API. По введённому адресу мы не запрашиваем ни favicon, ни заголовок, ни превью.
        </p>
        <p>
          Шрифты, кодировщик и WASM-декодер поставляются вместе с приложением: обращений к Google
          Fonts, jsDelivr или внешним QR-сервисам нет.
        </p>
        <p className={styles.caution}>
          Точная формулировка: содержимое QR обрабатывается в вашем браузере и не отправляется
          сервису. При этом хостинг, как и для любого сайта, видит обычные запросы страницы и её
          файлов.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Проверка готового файла</h2>
        <ol className={styles.list}>
          <li>Файл создаётся из того же макета, который вы видите в предпросмотре.</li>
          <li>Готовый PNG или JPEG читается независимым декодером; SVG для этого растеризуется.</li>
          <li>Прочитанная строка сравнивается с ожидаемым содержимым.</li>
          <li>Только после совпадения появляется ссылка на скачивание.</li>
        </ol>
        <p className={styles.caution}>
          Успешная проверка означает одно: этот цифровой файл прочитался. Она не гарантирует
          сканирование после печати, уменьшения, размытия, бликов и съёмки под углом. Если декодер
          недоступен, мы прямо пишем, что проверка не выполнялась, и не выдаём это за успех.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Читаемость</h2>
        <ul className={styles.list}>
          <li>
            Защитное поле — не меньше {MIN_QUIET_ZONE} модулей с каждой стороны. Рамки, плашки и
            подписи всегда остаются снаружи него.
          </li>
          <li>
            Модули рисуются по целой пиксельной сетке: moduleSize = floor(ширина / (модули + 2 ×
            поле)). Остаток уходит во внешний отступ, а не растягивает код.
          </li>
          <li>Файлы с модулем меньше {MIN_MODULE_PX} px не отдаются.</li>
          <li>
            Поле непосредственно под кодом по умолчанию белое, независимо от цвета остальной
            карточки и от темы интерфейса.
          </li>
          <li>
            Проверка контраста — эвристика. Пороги WCAG написаны для текста и не являются стандартом
            сертификации QR, поэтому никакого «процента надёжности» мы не показываем.
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Кодировка</h2>
        <p>
          Для данных вне ASCII используется UTF-8 с явным сегментом ECI 26 — так сканеру не нужно
          угадывать кодировку. Чистый ASCII кодируется обычными режимами, чтобы символ оставался
          компактным. Пробелы, переводы строк и невидимые символы сохраняются: если что-то выглядит
          подозрительно, мы предупреждаем, но ничего не исправляем молча.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Экспорт</h2>
        <ul className={styles.list}>
          <li>PNG — основной растровый формат, до {MAX_RASTER_SIDE} px по стороне.</li>
          <li>
            JPEG — для систем, которые не принимают PNG. Фон всегда непрозрачный, после сжатия файл
            обязательно декодируется заново.
          </li>
          <li>
            SVG — самостоятельный файл: подпись переведена в контуры, внешних ссылок и подключаемых
            шрифтов нет.
          </li>
          <li>
            После экспорта мы сверяем фактический MIME-тип: браузер иногда возвращает PNG вместо
            запрошенного формата, и выдавать такой файл под расширением .jpg нельзя.
          </li>
        </ul>
        <p className={styles.caution}>
          Расчёт «мм → px» даёт только размер в пикселях. Стандартный браузерный canvas записывает
          в метаданные 96 dpi, поэтому для точных физических размеров лучше SVG. CMYK и
          типографскую цветопробу мы не поддерживаем.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Хранение</h2>
        <p>
          По умолчанию содержимое живёт только в памяти вкладки и исчезает при её закрытии. Отдельно
          сохраняется лишь выбранная тема интерфейса. Кнопка «Сохранить проект» создаёт JSON по
          вашему явному действию; в этом файле есть введённое содержимое QR, поэтому храните его так
          же внимательно, как сам адрес. Имена файлов не содержат вашу ссылку.
        </p>
        <p className={styles.caution}>
          Удалить уже скачанные вами файлы мы не можем — они принадлежат вашему устройству.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Что внутри</h2>
        <ul className={styles.list}>
          <li>
            Кодировщик QR — библиотека Nayuki (TypeScript, MIT), включённая в исходники с указанием
            коммита и лицензии.
          </li>
          <li>Проверка считывания — zxing-wasm, WASM-файл лежит рядом с приложением.</li>
          <li>Перевод подписи в контуры — opentype.js.</li>
          <li>
            Шрифты: {FONT_FAMILIES.map((f) => f.name).join(', ')} — все под SIL Open Font License
            1.1, тексты лицензий лежат в папке licenses.
          </li>
        </ul>
        <p className={styles.build}>
          Сборка {APP_VERSION} от {BUILD_TIME}
        </p>
        <p className={styles.caution}>
          Версию видно не для красоты: без неё нельзя сказать, какой именно код у вас выполняется
          и что в нём внутри.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Чего здесь нет</h2>
        <ul className={styles.list}>
          <li>Динамических ссылок и статистики сканирований.</li>
          <li>Сокращателя ссылок и промежуточных переходов.</li>
          <li>Аккаунтов, облачной истории и серверной части.</li>
          <li>Логотипа внутри кода: перекрытие служебных узоров требует отдельной проверки.</li>
        </ul>
      </section>
    </article>
  );
}
